import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import NumFlowWithFallback from "../../../components/Common/NumFlowWithFallback";
import clsx from "clsx";
import { PriceImpact } from "./PriceImpact";
import { useUsdModeStore } from "../../../stores/usdModeStore";
export const ReceiveAmount = ({ destination_token, fee, isFeeLoading }) => {
    const isUsdMode = useUsdModeStore(s => s.isUsdMode);
    const receive_amount = fee?.quote.receive_amount;
    const receiveAmountInUsd = useMemo(() => {
        if (!receive_amount || !destination_token || !fee.quote?.destination_token?.price_in_usd) {
            return undefined;
        }
        return (receive_amount * fee.quote.destination_token.price_in_usd).toFixed(2);
    }, [receive_amount, destination_token, fee?.quote?.destination_token?.price_in_usd]);
    const quote = fee?.quote;
    const tokenDecimals = fee?.quote.destination_token?.decimals || 2;
    const primaryEmpty = isUsdMode ? !receiveAmountInUsd : !receive_amount;
    const containerRef = useRef(null);
    const priceImpactRef = useRef(null);
    const numberSpanRef = useRef(null);
    const [maxDecimals, setMaxDecimals] = useState(Math.min(tokenDecimals, 7));
    const canvasRef = useRef(null);
    useEffect(() => {
        const container = containerRef.current;
        const numberSpan = numberSpanRef.current;
        if (!container || !numberSpan || !isUsdMode)
            return;
        if (!canvasRef.current)
            canvasRef.current = document.createElement('canvas');
        const ctx = canvasRef.current.getContext('2d');
        if (!ctx)
            return;
        const calculate = () => {
            const containerWidth = container.clientWidth;
            const priceImpactWidth = priceImpactRef.current?.offsetWidth || 0;
            const gap = priceImpactWidth > 0 ? 8 : 0;
            const availableWidth = containerWidth - priceImpactWidth - gap;
            const style = getComputedStyle(numberSpan);
            ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            const intPart = receive_amount ? Math.floor(receive_amount).toString() : '0';
            const suffix = ` ${destination_token?.symbol || ''}`;
            const fixedWidth = ctx.measureText(intPart + '.').width + ctx.measureText(suffix).width;
            if (fixedWidth >= availableWidth) {
                setMaxDecimals(0);
                return;
            }
            const digitWidth = ctx.measureText('0').width;
            const fittingDigits = Math.floor((availableWidth - fixedWidth) / digitWidth);
            setMaxDecimals(Math.max(0, Math.min(fittingDigits, tokenDecimals)));
        };
        calculate();
        const observer = new ResizeObserver(calculate);
        observer.observe(container);
        return () => observer.disconnect();
    }, [isUsdMode, receive_amount, destination_token?.symbol, tokenDecimals]);
    useEffect(() => {
        if (!isUsdMode)
            setMaxDecimals(Math.min(tokenDecimals, 7));
    }, [isUsdMode, tokenDecimals]);
    return (_jsxs("div", { className: "flex-col w-full flex min-w-0 font-normal border-0 text-[28px] leading-7 text-primary-text relative truncate", children: [_jsx("div", { className: "w-full flex items-center justify-start relative", children: _jsx("div", { className: clsx("w-full flex items-center py-[3px] pr-3", { "animate-pulse-stronger": isFeeLoading }, { "text-secondary-text": primaryEmpty }), children: _jsx("div", { className: "min-h-11 inline-flex items-center", children: isUsdMode ? _jsx(_Fragment, { children: _jsx(NumFlowWithFallback, { prefix: "$", value: Number(receiveAmountInUsd) || 0, trend: 0, format: { minimumFractionDigits: 2, maximumFractionDigits: 2 } }) }) : (_jsx(NumFlowWithFallback, { value: receive_amount || 0, trend: 0, format: { maximumFractionDigits: tokenDecimals } })) }) }) }), _jsxs("div", { ref: containerRef, className: "flex items-baseline space-x-2", children: [_jsx("span", { ref: numberSpanRef, className: "text-xs sm:text-base leading-5 inline-flex items-center font-medium text-secondary-text h-5 min-w-0", children: isUsdMode ? _jsx(_Fragment, { children: _jsx(NumFlowWithFallback, { className: "p-0 align-middle", suffix: ` ${destination_token?.symbol || ''}`, value: receive_amount || 0, trend: 0, format: { maximumFractionDigits: maxDecimals } }) }) : (_jsx(NumFlowWithFallback, { className: "p-0 align-middle", value: Number(receiveAmountInUsd) || 0, prefix: "$", format: { maximumFractionDigits: receiveAmountInUsd ? 2 : 0 }, trend: 0 })) }), _jsx("span", { ref: priceImpactRef, className: "shrink-0", children: _jsx(PriceImpact, { className: "h-5 text-xs sm:text-base leading-5", quote: quote, refuel: fee?.refuel }) })] })] }));
};
